import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import mlService from '../services/mlService';
import './Estimate.css';

const Estimate = ({ onClose, modelId }) => {
    const [modelData, setModelData] = useState(null);
    const [mlModel, setMlModel] = useState(null);
    const [mlStats, setMlStats] = useState(null);
    const [mlUniqueValues, setMlUniqueValues] = useState(null);
    const [prediction, setPrediction] = useState(null);
    const [formData, setFormData] = useState({
        year: '',
        mileage: '',
        transmission: '',
        color: '',
        interiorColor: '',
        options: [],
        accident: '',
        conditionType: '',
        region: ''
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isTraining, setIsTraining] = useState(false);
    const [trainingProgress, setTrainingProgress] = useState(null);

    useEffect(() => {
        const fetchModelData = async () => {
            if (!modelId) return;
            
            try {
                setLoading(true);
                setError(null);
                setTrainingProgress(null);

                // Initialize ML service
                await mlService.initialize();

                const auctionsRef = collection(db, 'auctions');
                const q = query(auctionsRef, where('modelId', '==', modelId));
                const querySnapshot = await getDocs(q);
                
                // Process data for ML
                const auctionDataArray = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // Only process ML if we have enough data
                if (auctionDataArray.length >= 10) {
                    try {
                        setIsTraining(true);
                        setTrainingProgress('Preprocessing data...');
                        const { features, labels, stats, uniqueValues } = mlService.preprocessData(auctionDataArray);
                        
                        setTrainingProgress('Creating model...');
                        const model = mlService.createModel(features.shape[1]);
                        
                        setTrainingProgress('Training model...');
                        await mlService.trainModel(model, features, labels);
                        
                        setMlModel(model);
                        setMlStats(stats);
                        setMlUniqueValues(uniqueValues);
                        setTrainingProgress('Training complete');
                    } catch (err) {
                        console.error('Error training ML model:', err);
                        setError('Failed to train prediction model: ' + err.message);
                    } finally {
                        setIsTraining(false);
                    }
                } else {
                    setError('Not enough data to train a prediction model. Need at least 10 auctions.');
                }

                const years = new Set();
                const colors = new Set();
                const interiorColors = new Set();
                const regions = new Set();
                const transmissions = new Set();
                const allOptions = new Set();
                const accidents = new Set();
                const conditionTypes = new Set();

                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    if (data.year) years.add(data.year);
                    if (data.colorFamily) colors.add(data.colorFamily);
                    if (data.interiorColor) interiorColors.add(data.interiorColor);
                    if (data.country) regions.add(data.country);
                    if (data.transmission) transmissions.add(data.transmission);
                    if (data.options && Array.isArray(data.options)) {
                        data.options.forEach(opt => allOptions.add(opt));
                    }
                    // Convert boolean values directly to Yes/No
                    if (data.accident !== undefined) accidents.add(data.accident ? 'Yes' : 'No');
                    if (data.conditionType) conditionTypes.add(data.conditionType);
                });

                // Ensure we have at least one condition type
                if (conditionTypes.size === 0) {
                    conditionTypes.add('Standard');
                }

                // Only include fields that have more than one unique value
                const modelDataObj = {};
                
                if (years.size > 1) modelDataObj.years = Array.from(years).sort((a, b) => b - a);
                if (colors.size > 1) modelDataObj.colors = Array.from(colors).sort();
                if (interiorColors.size > 1) modelDataObj.interiorColors = Array.from(interiorColors).sort();
                if (regions.size > 1) modelDataObj.regions = Array.from(regions).sort();
                if (transmissions.size > 1) modelDataObj.transmissions = Array.from(transmissions).sort();
                // Only include options if there are multiple options available
                if (allOptions.size > 1) modelDataObj.options = Array.from(allOptions).sort();
                if (accidents.size > 1) modelDataObj.accidents = Array.from(accidents).sort();
                if (conditionTypes.size > 1) modelDataObj.conditionTypes = Array.from(conditionTypes).sort();

                setModelData(modelDataObj);

            } catch (err) {
                console.error('Error fetching model data:', err);
                setError('Failed to load model data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchModelData();
    }, [modelId]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleOptionsChange = (selectedOptions) => {
        setFormData(prev => ({
            ...prev,
            options: selectedOptions ? selectedOptions.map(option => option.value) : []
        }));
    };

    const handleEstimate = async () => {
        if (!mlModel || !mlStats || !mlUniqueValues) {
            setError('Not enough data to make a prediction');
            return;
        }

        try {
            // Validate required fields
            if (!formData.year || !formData.mileage || !formData.region || 
                !formData.color || !formData.interiorColor) {
                setError('Please fill in all required fields');
                return;
            }

            // Convert mileage to number and validate
            const mileage = parseFloat(formData.mileage);
            if (isNaN(mileage) || mileage < 0) {
                setError('Please enter a valid mileage');
                return;
            }

            // Prepare input data
            const inputData = {
                year: formData.year,
                mileage: formData.mileage,
                country: formData.region, // Map region to country for backend compatibility
                color: formData.color,
                interiorColor: formData.interiorColor,
                transmission: formData.transmission || '',
                accident: formData.accident || 'No',
                conditionType: formData.conditionType || '',
                options: formData.options || []
            };

            console.log('Making prediction with input:', inputData);

            const estimatedPrice = await mlService.predictPrice(mlModel, inputData, mlStats, mlUniqueValues);
            
            console.log('Received prediction:', estimatedPrice);

            setPrediction({
                estimate: estimatedPrice,
                range: {
                    low: estimatedPrice * 0.9,
                    high: estimatedPrice * 1.1
                }
            });
        } catch (err) {
            console.error('Error making prediction:', err);
            setError('Failed to generate estimate: ' + err.message);
        }
    };

    const createOptionsArray = (options) => {
        if (!options) return [];
        return options.map(option => ({
            value: option,
            label: option
        }));
    };

    if (loading) {
        return (
            <div className="estimate-popup">
                <div className="estimate-content">
                    <span className="close-icon" onClick={onClose}>&#10005;</span>
                    <div className="training-status">
                        <div className="loading-spinner"></div>
                        <p>Training Machine Learning Model...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="estimate-popup">
                <div className="estimate-content">
                    <span className="close-icon" onClick={onClose}>&#10005;</span>
                    <div className="error">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="estimate-popup">
            <div className="estimate-content">
                <span className="close-icon" onClick={onClose}>&#10005;</span>
                <h3>Estimate Value</h3>
                
                {isTraining && (
                    <div className="training-status">
                        <div className="loading-spinner"></div>
                        <p>{trainingProgress || 'Training Machine Learning Model...'}</p>
                    </div>
                )}

                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}
                
                {prediction && (
                    <div className="prediction-result">
                        <h4>Estimate: ${Math.round(prediction.range.low).toLocaleString()} - ${Math.round(prediction.range.high).toLocaleString()}</h4>
                    </div>
                )}
                
                {modelData && (
                    <div className="estimate-form">
                        {/* Year */}
                        {modelData.years && (
                            <div className="form-group">
                                <label>Year</label>
                                <select 
                                    value={formData.year}
                                    onChange={(e) => handleInputChange('year', e.target.value)}
                                >
                                    <option value="">Select Year</option>
                                    {modelData.years.map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Mileage */}
                        <div className="form-group">
                            <label>Mileage</label>
                            <input 
                                type="number" 
                                value={formData.mileage}
                                onChange={(e) => handleInputChange('mileage', e.target.value)}
                                placeholder="Enter mileage"
                            />
                        </div>

                        {/* Exterior Color */}
                        {modelData.colors && (
                            <div className="form-group">
                                <label>Exterior Color</label>
                                <select 
                                    value={formData.color}
                                    onChange={(e) => handleInputChange('color', e.target.value)}
                                >
                                    <option value="">Select Exterior Color</option>
                                    {modelData.colors.map(color => (
                                        <option key={color} value={color}>{color}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Interior Color */}
                        {modelData.interiorColors && (
                            <div className="form-group">
                                <label>Interior Color</label>
                                <select 
                                    value={formData.interiorColor}
                                    onChange={(e) => handleInputChange('interiorColor', e.target.value)}
                                >
                                    <option value="">Select Interior Color</option>
                                    {modelData.interiorColors.map(color => (
                                        <option key={color} value={color}>{color}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Region */}
                        {modelData.regions && (
                            <div className="form-group">
                                <label>Region</label>
                                <select 
                                    value={formData.region}
                                    onChange={(e) => handleInputChange('region', e.target.value)}
                                >
                                    <option value="">Select Region</option>
                                    {modelData.regions.map(region => (
                                        <option key={region} value={region}>{region}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Accident History */}
                        <div className="form-group">
                            <label>Accident History</label>
                            <select 
                                value={formData.accident}
                                onChange={(e) => handleInputChange('accident', e.target.value)}
                            >
                                <option value="">Select Accident History</option>
                                <option value="false">No</option>
                                <option value="true">Yes</option>
                            </select>
                        </div>

                        {/* Condition Type */}
                        {modelData.conditionTypes && (
                            <div className="form-group">
                                <label>Condition Type</label>
                                <select 
                                    value={formData.conditionType}
                                    onChange={(e) => handleInputChange('conditionType', e.target.value)}
                                >
                                    <option value="">Select Condition Type</option>
                                    {modelData.conditionTypes.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Options */}
                        {modelData.options && modelData.options.length > 1 && (
                            <div className="form-group">
                                <label>Options</label>
                                <Select
                                    isMulti
                                    name="options"
                                    options={createOptionsArray(modelData.options)}
                                    className="options-select"
                                    value={formData.options.map(option => ({ value: option, label: option }))}
                                    onChange={handleOptionsChange}
                                    placeholder="Select options (optional)"
                                />
                            </div>
                        )}

                        {/* Transmission */}
                        {modelData.transmissions && (
                            <div className="form-group">
                                <label>Transmission</label>
                                <select 
                                    value={formData.transmission}
                                    onChange={(e) => handleInputChange('transmission', e.target.value)}
                                >
                                    <option value="">Select Transmission</option>
                                    {modelData.transmissions.map(transmission => (
                                        <option key={transmission} value={transmission}>{transmission}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <button 
                            className="estimate-button"
                            onClick={handleEstimate}
                        >
                            Get Estimate
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Estimate;
